/**
 * comandi.js — la logica della barra comandi (Ctrl K), senza React.
 *
 * Sta qui e non nel componente perche' si prova da sola (comandi.test.js):
 * che cosa la barra offre per un testo scritto, in che ordine, e quando un
 * Ctrl K e' suo e quando e' del campo in cui si sta scrivendo.
 *
 * CHE COSA OFFRE LA BARRA lo decide la tabella delle schermate
 * (navigazione/schermate.js): il «vai a» e' vociVaiA(utente), le azioni
 * azioniDi(utente), gia' filtrate per ruolo con la stessa funzione delle
 * rotte. Qui si filtra soltanto per il TESTO scritto, mai per il ruolo: un
 * secondo filtro per ruolo sarebbe una seconda regola, e due regole
 * divergono al primo ritocco.
 */
import { schermataDi, GRUPPI, SCHERMATE, nelGuscio } from './schermate';

/** Quanti caratteri prima di cercare un paziente, e quanti se ne mostrano. */
export const MINIMO_PAZIENTI = 2;
export const LIMITE_PAZIENTI = 8;

/**
 * Minuscole, senza accenti e con gli spazi ridotti: «Attivita'» e
 * «attivita» si trovano a vicenda, come chi scrive di fretta si aspetta.
 */
export function normalizza(testo) {
    return String(testo == null ? '' : testo)
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}

/* Le parole di un nome: si spezza anche sull'apostrofo, cosi'
   «dell'assistente» comincia anche con «assistente». */
const paroleDi = (n) => n.split(/[\s'’\-/]+/).filter(Boolean);

/**
 * Quanto un elenco di nomi corrisponde al testo: un numero piu' basso e'
 * meglio, null vuol dire che non corrisponde. Il primo nome e' quello
 * vero (l'etichetta); gli altri - i nomi di prima, i sinonimi - valgono
 * meno, cosi' «fatt» mette «Fatture» prima di una voce trovata per un
 * suo vecchio nome.
 *   0  il nome comincia col testo
 *   1  una parola del nome comincia col testo
 *   2  ogni parola scritta sta da qualche parte nel nome
 */
export function punteggio(testo, nomi) {
    const q = normalizza(testo);
    if (!q) return 0;
    const cercate = q.split(' ');
    let migliore = null;
    nomi.forEach((nome, i) => {
        const n = normalizza(nome);
        if (!n) return;
        let p = null;
        if (n.startsWith(q)) p = 0;
        else if (paroleDi(n).some((w) => w.startsWith(q))) p = 1;
        else if (cercate.every((w) => n.includes(w))) p = 2;
        if (p === null) return;
        const conPeso = p + (i === 0 ? 0 : 3);
        if (migliore === null || conPeso < migliore) migliore = conPeso;
    });
    return migliore;
}

/* Filtra e ordina; a parita' resta l'ordine della tabella. Ogni voce
   tiene il suo punteggio: la barra sceglie per prima la migliore di tutte,
   non la prima disegnata. `punteggioDi` puo' restituire null (non c'entra). */
function filtra(elenco, testo, punteggioDi) {
    const vuoto = !normalizza(testo);
    return elenco
        .map((x, i) => ({ x, i, p: vuoto ? 0 : punteggioDi(x) }))
        .filter((r) => r.p !== null)
        .sort((a, b) => a.p - b.p || a.i - b.i);
}

/* Un'azione trovata solo coi nomi della SUA SCHERMATA vale meno della
   schermata stessa: chi scrive «fatt» o «Fatturazione» vuole andare su
   Fatture, e Invio deve portarlo li', non aprirgli un documento nuovo.
   L'azione resta nell'elenco (si scopre che c'e'), ma non e' la scelta. */
const PESO_NOMI_DELLA_SCHERMATA = 6;

const etichettaGruppo = (codice) => (GRUPPI.find((g) => g.codice === codice) || {}).etichetta || '';

/**
 * Le voci di «Azioni» e «Vai a» per questo testo, gia' nella forma che la
 * barra disegna: { tipo, chiave, etichetta, suggerimento, percorso, stato }.
 *   azioni   azioniDi(utente)
 *   vaiA     vociVaiA(utente)
 * Un'azione si trova anche col nome della sua schermata («fatt» trova
 * «Nuovo documento», che sta in Fatture).
 */
export function vociComandi({ testo, azioni, vaiA }) {
    const az = filtra(azioni, testo, (a) => {
        const proprio = punteggio(testo, [a.etichetta]);
        if (proprio !== null) return proprio;
        const s = schermataDi(a.percorso) || {};
        const p = punteggio(testo, [s.etichetta, ...(s.cerca || [])]);
        return p === null ? null : p + PESO_NOMI_DELLA_SCHERMATA;
    }).map(({ x: a, p }) => ({
        tipo: 'azione',
        punteggio: p,
        chiave: `azione:${a.codice}`,
        etichetta: a.etichetta,
        suggerimento: (schermataDi(a.percorso) || {}).etichetta || '',
        percorso: a.percorso,
        stato: a.stato,
        codice: a.codice,
    }));
    const vai = filtra(vaiA, testo, (s) => punteggio(testo, [s.etichetta, ...(s.cerca || [])])).map(({ x: s, p }) => ({
        tipo: 'vai',
        punteggio: p,
        chiave: `vai:${s.percorso}`,
        etichetta: s.etichetta,
        suggerimento: etichettaGruppo(s.gruppo),
        percorso: s.percorso,
        stato: null,
    }));
    return { azioni: az, vaiA: vai };
}

/* Le parole di ogni nome del gestionale: etichette, nomi di prima,
   linguette e azioni di TUTTE le schermate del guscio, e i gruppi. Si
   calcolano una volta: la tabella non cambia mentre la pagina e' aperta. */
let nomiDelGestionale = null;
function paroleDeiNomi() {
    if (!nomiDelGestionale) {
        const nomi = [...GRUPPI.map((g) => g.etichetta)];
        for (const s of SCHERMATE) {
            if (!nelGuscio(s)) continue;
            nomi.push(s.etichetta, s.linguetta, ...(s.cerca || []), ...(s.azioni || []).map((a) => a.etichetta));
        }
        nomiDelGestionale = nomi.filter(Boolean).map((n) => paroleDi(normalizza(n)));
    }
    return nomiDelGestionale;
}

/**
 * Il testo scritto e' il nome di una schermata o di un'azione, o il suo
 * inizio? Lo e' se ogni parola scritta comincia una parola dello STESSO
 * nome: «fatt», «Fatture», «reg mod», «nuovo doc», «dell'assistente».
 *
 * PERCHE' (2/10/2026, dalla revisione del ramo console). Il «vai a» e' l'uso
 * piu' frequente della barra, e la ricerca dei pazienti partiva su
 * qualunque testo: chi scriveva «fatture», si fermava un attimo e premeva
 * Invio lasciava nel registro degli accessi una «ricerca» di pazienti con
 * termine «fatture», e si vedeva passare sotto gli occhi nomi di pazienti
 * che non aveva cercato. Su un testo cosi' la barra non cerca da sola:
 * offre «Cerca fra i pazienti», e cerca solo se la si sceglie.
 *
 * Si guardano TUTTE le schermate del gestionale, anche quelle che il ruolo
 * non apre: un medico che scrive «fatture» non sta cercando un paziente
 * che si chiama cosi'. Non si offre niente in piu': e' solo la decisione
 * di non cercare da sola.
 */
export function nomeDiSchermata(testo) {
    const cercate = paroleDi(normalizza(testo));
    if (!cercate.length) return false;
    return paroleDeiNomi().some((parole) => cercate.every((w) => parole.some((p) => p.startsWith(w))));
}

/* La voce che chiede di cercare fra i pazienti: l'ultima scelta possibile,
   mai la prima quando c'e' altro. */
const PUNTEGGIO_SU_RICHIESTA = 100;

/**
 * La voce «Cerca fra i pazienti», per un testo che e' il nome di una
 * schermata (nomeDiSchermata): sceglierla fa partire la ricerca della
 * pagina Pazienti, la stessa chiamata e la stessa traccia.
 */
export function voceCercaPazienti(testo) {
    return {
        tipo: 'cerca',
        punteggio: PUNTEGGIO_SU_RICHIESTA,
        chiave: 'cerca-pazienti',
        etichetta: 'Cerca fra i pazienti',
        suggerimento: `«${String(testo || '').trim()}»`,
        percorso: null,
        stato: null,
    };
}

/**
 * Un paziente come lo mostra la barra: le iniziali, il nome come lo scrive
 * l'agenda (cognome e nome) e il codice del paziente. Niente di piu' di
 * quello che l'elenco dei pazienti gia' mostra.
 *
 * Il punteggio: il server l'ha gia' trovato (anche per codice fiscale, che
 * qui non si guarda), quindi conta almeno come «le parole ci sono» (2); se
 * il nome o il codice cominciano col testo, quanto una schermata.
 */
export function vocePaziente(p, testo = '') {
    const iniziali = `${(p.cognome || '').trim().charAt(0)}${(p.nome || '').trim().charAt(0)}`.toUpperCase();
    const nomi = [`${p.cognome || ''} ${p.nome || ''}`, `${p.nome || ''} ${p.cognome || ''}`, p.id_paziente || ''];
    const trovato = nomi.map((n) => punteggio(testo, [n])).filter((x) => x !== null);
    return {
        tipo: 'paziente',
        punteggio: Math.min(2, ...trovato),
        chiave: `paziente:${p.id}`,
        etichetta: [p.cognome, p.nome].filter(Boolean).join(' '),
        suggerimento: p.id_paziente || '',
        iniziali,
        percorso: `/pazienti/${p.id}`,
        stato: null,
        id: p.id,
    };
}

/**
 * La voce da scegliere per prima: la migliore di tutte, e a parita' la
 * prima disegnata (le sezioni sono Azioni, Vai a, Pazienti).
 */
export function indiceMigliore(voci) {
    let migliore = -1;
    voci.forEach((v, i) => {
        if (migliore < 0 || v.punteggio < voci[migliore].punteggio) migliore = i;
    });
    return migliore;
}

/** Il sistema e' un Mac? Li' la scorciatoia si scrive «⌘ K». */
export function eMac(nav = (typeof navigator !== 'undefined' ? navigator : null)) {
    if (!nav) return false;
    const p = (nav.userAgentData && nav.userAgentData.platform) || nav.platform || '';
    return /mac/i.test(p);
}

/** E' Ctrl K (o Cmd K)? Non con Alt (AltGr e' Ctrl+Alt) ne' con Maiuscole. */
export function eCtrlK(e) {
    if (!e || e.altKey || e.shiftKey || e.isComposing) return false;
    if (!(e.ctrlKey || e.metaKey)) return false;
    return String(e.key || '').toLowerCase() === 'k';
}

const eScrivibile = (el) => !!el && (
    el.isContentEditable
    || el.tagName === 'TEXTAREA'
    || (el.tagName === 'INPUT' && !['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color'].includes(el.type))
);

/**
 * Il Ctrl K e' del campo in cui si sta scrivendo, non della barra?
 *
 * Tre casi, e in tutti la barra lo lascia stare:
 *   - il campo l'ha gia' usato: chi lo gestisce chiama preventDefault, e
 *     la barra ascolta DOPO (sulla finestra, in risalita). E' la regola
 *     generale: un editor che domani legasse Ctrl K a «inserisci un
 *     collegamento» non deve far niente di piu' per tenerselo;
 *   - il campo lo dichiara: data-tiene-ctrl-k su di lui o su un antenato;
 *   - su un Mac, Ctrl K in un campo di testo e' «cancella fino a fine
 *     riga» del sistema: li' la barra si apre con Cmd K.
 */
export function campoTieneCtrlK(e, mac = eMac()) {
    if (!e) return false;
    if (e.defaultPrevented) return true;
    const t = e.target;
    if (t && typeof t.closest === 'function' && t.closest('[data-tiene-ctrl-k]')) return true;
    if (mac && e.ctrlKey && !e.metaKey && eScrivibile(t)) return true;
    return false;
}
